import type {
  InvalidateQueryFilters,
  QueryClient,
} from '@tanstack/react-query';

/** Wildcard marker for `invalidateQueriesByKeyPattern` — matches any value at that key position. */
export const ANY_KEY_SEGMENT = '*' as const;

/**
 * A query-key pattern to match against cached queries. Each element is
 * either an exact value a key segment must equal at that position, or
 * `ANY_KEY_SEGMENT` ('*') to match any value — e.g. an id you don't know
 * or don't care about.
 *
 * Only the positions present in the pattern are checked; a cached key can
 * be longer than the pattern (extra trailing segments are ignored).
 */
export type QueryKeyPattern = readonly (
  | string
  | number
  | typeof ANY_KEY_SEGMENT
)[];

/**
 * Invalidates every cached query whose key matches `pattern`, position by
 * position, treating `ANY_KEY_SEGMENT` entries as wildcards. Useful when you
 * need to invalidate a query by shape (e.g. "the 'state' query for whichever
 * customer is cached") without resolving or trusting a specific id.
 *
 * @param queryClient - The React Query client instance
 * @param pattern - Key segments to match; `ANY_KEY_SEGMENT` matches any value at that position
 * @param filters - Additional `invalidateQueries` filters (e.g. `refetchType`) merged alongside the pattern predicate
 *
 * @example
 * ```typescript
 * // Invalidate ['customer', <any customerId>, 'state'] regardless of which
 * // customer id is currently cached.
 * invalidateQueriesByKeyPattern(queryClient, ['customer', ANY_KEY_SEGMENT, 'state']);
 * ```
 */
export function invalidateQueriesByKeyPattern(
  queryClient: QueryClient,
  pattern: QueryKeyPattern,
  filters?: Omit<InvalidateQueryFilters, 'predicate' | 'queryKey'>,
): void {
  queryClient.invalidateQueries({
    ...filters,
    predicate: (query) =>
      pattern.every(
        (segment, index) =>
          segment === ANY_KEY_SEGMENT || query.queryKey[index] === segment,
      ),
  });
}
